import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, TextField } from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { RefundServiceV2 } from '../../services/RefundServiceV2';
import { RazorpayOrderReferenceModelEnums } from '../../enums/RazorpayOrderReferenceModelEnums';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';
import { useAppSelector } from '../../store/hooks';
import GroupPermissionFunction from '../../utils/groupPermissionFunction';
import { UserGroupsEnum } from '../../enums/UserGroupsEnum';
import moment from 'moment';

interface InputProps {
    houseId: number;
}

const refundServiceV2 = new RefundServiceV2();

const HousesRefundsTab = ({ houseId }: InputProps) => {
    const userDetails = useAppSelector((state) => state.userDetails.value);
    const groupsArray: string[] = userDetails.adminUser.accessGroups;

    const [modelName, setModelName] = useState<RazorpayOrderReferenceModelEnums>(RazorpayOrderReferenceModelEnums.USER_BUNDLE_PAYMENTS);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('0');
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);

    const refreshData = useCallback(() => {
        setLoading(true);
        refundServiceV2
            .getRefundsForHouse(houseId, modelName, rowsPerPage, page * rowsPerPage)
            .then((res) => {
                setData(res?.data ?? []);
                setTotal(res?.total ?? 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [houseId, modelName, page, rowsPerPage]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    useEffect(() => {
        setPage(0);
    }, [modelName]);

    const handleSubmit = () => {
        setSubmitting(true);
        if (Number(amount) < 0) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Enter a valid amount', type: CustomSnackbarTypes.ERROR });
            setSubmitting(false);
            return;
        }
        if (!GroupPermissionFunction(groupsArray, [UserGroupsEnum.MANAGER])) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Only managers have access to initiate refund', type: CustomSnackbarTypes.ERROR });
            setSubmitting(false);
            return;
        }
        if (Number(selectedRow?.refundableAmount) === 0) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Total amount already refunded', type: CustomSnackbarTypes.ERROR });
            setSubmitting(false);
            return;
        }
        if (Number(selectedRow?.refundableAmount) < Number(amount)) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: `Amount exceeds refundable amount (${selectedRow?.refundableAmount})`, type: CustomSnackbarTypes.ERROR });
            setSubmitting(false);
            return;
        }
        refundServiceV2
            .createRefundForOrder(selectedRow?.razorpayOrderId, { refundAmount: amount })
            .then(() => {
                setSubmitting(false);
                setOpen(false);
                setSelectedRow(null);
                refreshData();
            })
            .catch(() => setSubmitting(false));
    };

    const columns: CommonListColumn[] = [
        { label: 'Id', field: 'id' },
        { label: 'Reference Id', field: 'referenceId' },
        { label: 'Reference Model', field: 'referenceModelName' },
        { label: 'Payment Id', field: 'paymentId' },
        { label: 'Refund Id', field: 'refundId' },
        { label: 'Razorpay Id', field: 'razorpayOrderId' },
        {
            label: 'RRN',
            render: (row) => row?.vendorRefundResponse?.acquirer_data?.rrn || row?.vendorRefundResponse?.acquirer_data?.arn || '-',
        },
        { label: 'Amount', field: 'amount' },
        { label: 'Refund Type', field: 'refundType' },
        { label: 'Refundable Amount', field: 'refundableAmount' },
        { label: 'Status', field: 'status', type: 'status' },
        {
            label: 'Created At',
            render: (row) => row?.createdAt ? moment(row?.createdAt).format('YYYY/MM/DD HH:mm') : '-',
        },
        {
            label: 'Updated At',
            render: (row) => row?.updatedAt ? moment(row?.updatedAt).format('YYYY/MM/DD HH:mm') : '-',
        },
        {
            label: 'Actions',
            stopRowClick: true,
            render: (row) => (
                <Button
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={row?.refundType === 'FULL'}
                    onClick={() => {
                        setSelectedRow(row);
                        setAmount('0');
                        setOpen(true);
                    }}
                >
                    Refund
                </Button>
            ),
        },
    ];

    return (
        <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={modelName} onChange={(_, v) => setModelName(v)}>
                    <Tab label="Subscription" value={RazorpayOrderReferenceModelEnums.USER_BUNDLE_PAYMENTS} />
                    <Tab label="One Meal" value={RazorpayOrderReferenceModelEnums.ONE_MEAL_SERVICE} />
                    <Tab label="Trial" value={RazorpayOrderReferenceModelEnums.TRIAL_V3} />
                    <Tab label="Coupon" value={RazorpayOrderReferenceModelEnums.DISCOUNT_COUPON_ORDER} />
                    <Tab label="Chef for party" value={RazorpayOrderReferenceModelEnums.CHEF_FOR_PARTY} />
                </Tabs>
            </Box>

            <CommonListTable
                columns={columns}
                data={data}
                loading={loading}
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                onPageChange={setPage}
                onRowsPerPageChange={setRowsPerPage}
                emptyText="No Refund Found"
            />

            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Refund Dialog</DialogTitle>
                <DialogContent>
                    <TextField
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        label="Enter refund amount"
                        type="number"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} color="error">Close</Button>
                    <Button disabled={submitting} onClick={handleSubmit} variant="contained">Submit</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default HousesRefundsTab;
